import { CheckOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button } from "antd";
import type React from "react";
import { useEffect, useState } from "react";
import { useIsMobileView } from "../../hooks/ui-hook";
import { getEHRTransferStatus } from "../../service/notes.service";
import type { EHRTransferStatus, TransferJob } from "../../types/index.type";
import { useOptionalTransfer } from "./TransferContext";

interface TransferToEHRButtonProps {
  // The note this button initiates a transfer for. Required so the global
  // TransferContext knows which audio to transfer, and so the button can tell
  // whether the user's active transfer is for THIS note vs. a different one.
  audioId: number;
  className?: string;
  icon?: React.ReactNode;
}

const ACTIVE_STATUSES = ["pending", "logging_in", "awaiting_mfa", "patient_lookup", "template_mapping", "writing_note"];
const AWAITING_SELECTION_STATUSES = new Set(["awaiting_appointment", "awaiting_note_type"]);

const TransferToEHRButton: React.FC<TransferToEHRButtonProps> = ({ audioId, className = "", icon: defaultIcon }) => {
  const isMobile = useIsMobileView();
  // useOptionalTransfer (not useTransfer) because this header is reused on
  // `/notesforMobile`, which is routed outside AuthLayout and therefore has
  // no TransferProvider. Throwing there crashed the whole subtree.
  const transfer = useOptionalTransfer();
  const activeJob = transfer?.activeJob ?? null;

  // Per-note historical status. The global TransferContext only knows about
  // *active* (or just-finished) jobs, so a note transferred yesterday looks
  // like "not_started" to it. We fetch this note's most recent job on mount
  // so a previously-transferred note correctly renders "Transferred".
  const [historicalJob, setHistoricalJob] = useState<TransferJob | null>(null);

  useEffect(() => {
    let cancelled = false;
    getEHRTransferStatus(audioId)
      .then((res) => {
        if (cancelled) return;
        setHistoricalJob(res.data?.job ?? null);
      })
      .catch((err) => console.error("Failed to fetch transfer history for note:", err));
    return () => {
      cancelled = true;
    };
  }, [audioId]);

  // When the live job is for THIS note and reaches terminal, persist it as
  // the historical state so the button stays in the right state even after
  // the active endpoint stops returning the job.
  useEffect(() => {
    if (activeJob?.audio_file_id === audioId && (activeJob.status === "completed" || activeJob.status === "failed")) {
      setHistoricalJob(activeJob);
    }
  }, [activeJob, audioId]);

  if (isMobile || !transfer) return null;
  const { isLoading, credentialStatus, currentStatus, isNoteTypeSelection, startTransfer } = transfer;

  // Effective status priority: live job for this note > historical > idle.
  const isActiveJobForThisNote = activeJob?.audio_file_id === audioId;
  const effectiveStatus: EHRTransferStatus = isActiveJobForThisNote
    ? currentStatus
    : (historicalJob?.status as EHRTransferStatus | undefined) ?? "not_started";

  const isInProgress = ACTIVE_STATUSES.includes(effectiveStatus);
  const isAwaitingSelection = AWAITING_SELECTION_STATUSES.has(effectiveStatus);
  const isAwaitingMFA = effectiveStatus === "awaiting_mfa";
  const isCompleted = effectiveStatus === "completed";
  const isFailed = effectiveStatus === "failed";
  const hasCredentials = credentialStatus?.has_credential ?? false;
  // Disabled if another note's transfer is in flight (single-active for v1).
  const otherTransferActive =
    !!activeJob && !isActiveJobForThisNote && activeJob.status !== "completed" && activeJob.status !== "failed";
  const isDisabled =
    isLoading || isInProgress || isCompleted || isAwaitingSelection || isAwaitingMFA || otherTransferActive;

  let buttonText = "Transfer to EHR";
  let icon: React.ReactNode = null;

  const isProcessingState = isLoading || isInProgress || isAwaitingSelection || isAwaitingMFA;
  const style = { color: "white" };
  if (isLoading) {
    buttonText = "Checking...";
    icon = <LoadingOutlined style={style} />;
  } else if (isCompleted) {
    buttonText = "Transferred";
    icon = <CheckOutlined style={style} />;
  } else if (isFailed) {
    buttonText = "Retry Transfer";
  } else if (otherTransferActive) {
    buttonText = "Another transfer running...";
    icon = <LoadingOutlined style={style} />;
  } else if (!hasCredentials) {
    buttonText = "Transfer to EHR";
  } else if (isInProgress) {
    buttonText = "Transferring...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingMFA) {
    buttonText = "Enter MFA Code...";
    icon = <LoadingOutlined style={style} />;
  } else if (isAwaitingSelection) {
    buttonText = isNoteTypeSelection ? "Select Note Template..." : "Select Appointment...";
    icon = <LoadingOutlined style={style} />;
  } else if (hasCredentials) {
    buttonText = "Transfer to EHR";
  }

  return (
    <Button
      type="primary"
      className={className}
      style={{
        display: "flex",
        alignItems: "center",
        ...((isProcessingState || isCompleted) && {
          backgroundColor: "#1677ff",
          borderColor: "#1677ff",
          color: "white",
          opacity: 1,
        }),
      }}
      onClick={() => startTransfer(audioId)}
      disabled={isDisabled}
      icon={icon ?? defaultIcon}
    >
      {buttonText}
    </Button>
  );
};

export default TransferToEHRButton;
